import { FC, ReactElement, useState } from "react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts";

export interface UtilizationTrendChartDataPoint {
  weekStartUtc: string;
  weekEndUtc: string;
  count: number;
  isCurrentPeriod: boolean;
}

export interface UtilizationTrendChartProps {
  data: UtilizationTrendChartDataPoint[];
  label: string;
}

interface HoveredDot {
  point: UtilizationTrendChartDataPoint;
  isCurrentPeriod: boolean;
  x: number;
  y: number;
}

interface DotRenderProps {
  cx?: number;
  cy?: number;
  payload?: UtilizationTrendChartDataPoint;
}

const currentPeriodSuffix = (isCurrentPeriod: boolean): string => isCurrentPeriod ? " (current week to date)" : "";

const formatUtcDate = (value: string): string =>
  new Date(value).toLocaleDateString("en-US", { day: "2-digit", month: "2-digit", timeZone: "UTC", year: "numeric" });

const formatDateRange = (point: UtilizationTrendChartDataPoint): string =>
  `${formatUtcDate(point.weekStartUtc)} - ${formatUtcDate(point.weekEndUtc)}`;

export const UtilizationTrendChart: FC<UtilizationTrendChartProps> = ({ data, label }) => {
  const [hoveredDot, setHoveredDot] = useState<HoveredDot | undefined>();

  if (data.length === 0) {
    return <p data-testid="utilization-trend-chart__empty">No utilization data available.</p>;
  }

  const formatAxisTick = (value: string): string => {
    const point = data.find(p => p.weekEndUtc === value);
    return point ? formatDateRange(point) : formatUtcDate(value);
  };

  const renderDot = (props: DotRenderProps): ReactElement => {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null || !payload) {
      return <></>;
    }

    const isCurrentPeriod = !!payload.isCurrentPeriod;
    const dotProps = {
      "aria-label": `Week of ${formatDateRange(payload)}: ${label} ${payload.count.toLocaleString()}${currentPeriodSuffix(isCurrentPeriod)}`,
      cx,
      cy,
      "data-testid": `utilization-trend-chart__dot-${payload.weekEndUtc}`,
      onBlur: () => setHoveredDot(undefined),
      onFocus: () => setHoveredDot({ isCurrentPeriod, point: payload, x: cx, y: cy }),
      onMouseEnter: () => setHoveredDot({ isCurrentPeriod, point: payload, x: cx, y: cy }),
      onMouseLeave: () => setHoveredDot(undefined),
      role: "img",
      tabIndex: 0,
    };

    // The current-period point is a running today-snapshot plotted off the weekly grid, not a closed
    // week's final tally, so it's rendered as a hollow ring rather than a filled dot to set it apart.
    return isCurrentPeriod
      ? <circle {...dotProps} r={5} fill="#ffffff" stroke="#056edf" strokeWidth={2} strokeDasharray="2 1" />
      : <circle {...dotProps} r={5} fill="#056edf" stroke="#ffffff" strokeWidth={1} />;
  };

  return (
    <div className="utilization-trend-chart" style={{ position: "relative" }}>
      <ResponsiveContainer width="100%" height={350}>
        <LineChart data={data} margin={{ bottom: 40, left: 30, right: 100, top: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="weekEndUtc" tickFormatter={formatAxisTick} angle={-30} textAnchor="end" height={60} />
          <YAxis label={{ angle: -90, position: "left", style: { textAnchor: "middle" }, value: label }} />
          <Line type="monotone" dataKey="count" stroke="#056edf" dot={renderDot} activeDot={false} isAnimationActive={false} name={label} />
        </LineChart>
      </ResponsiveContainer>
      {hoveredDot && (
        <div
          className="utilization-trend-chart__tooltip bg-white border rounded p-2"
          data-testid="utilization-trend-chart__tooltip"
          style={{ left: hoveredDot.x + 10, pointerEvents: "none", position: "absolute", top: hoveredDot.y - 10 }}
        >
          <div>Week: {formatDateRange(hoveredDot.point)}</div>
          <div>{label}: {hoveredDot.point.count.toLocaleString()}</div>
          {hoveredDot.isCurrentPeriod && (
            <div className="text-muted">Current week to date; this week has not yet ended.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default UtilizationTrendChart;
